import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Drawer, LoadingOverlay } from '@mantine/core';
import { FreightDetails, isShipmentCancelled } from '@shipstation/client-common/components/freight-details';
import { mapBackendShipmentToLtlShipment } from '@shipstation/client-common/ltl/freight-payload-mappers';
import { emptyArray } from 'ui-components/default-props/empty';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import Global from '../../../../../../store/global-selectors';
import { goToOrderDetails } from '../../../../../../store/orders/order-details/orders.details.actions';
import { useModalManagerPortal } from '../../../../../../portals/hooks';
import { downloadDocumentRequest, trackShipmentRequest } from '../../../../../../store/ltl/ltl.actions';
import { getFreightGridRequest } from '../../../../../../store/ltl/grid/ltl.grid.actions';
import { NOTE_TYPES } from '../../../../../../services/order-note-service';
import { useModal } from '../../../../../../hooks/use-modal';
import { useVoidFreight } from '../../../../../../hooks/freight/use-void-freight';
import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';
import { freightActionButtonMessages } from '../../../freight-action-buttons/messages';
import { bookingConfirmationViewMessages } from './booking-confirmation-view-messages';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../../../tracking-events';
import type { LtlFreightBookingSource } from '../../../../tracking-events';

import type { ShipFrom } from '@shipstation/client-common/types/ship-from';
import type { BaseWeightUnit, WeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type { FreightShipment } from '@shipstation/client-common/types/freight-responses';
import type { FreightTrackingHistory, OrderNote } from '@shipstation/client-common/components/freight-details';
import type { LtlAddress, LtlBookingConfirmation, LtlDocument } from '@shipstation/client-common/types/ltl';

import style from './booking-confirmation-view.module.less';

const DEFAULT_COUNTRY_CODE = 'US';

// Notes are stored one column per type, so a type holds at most one note and the pluralized
// note-type labels always resolve singular.
const SINGLE_NOTE_COUNT = 1;

type FulfillmentPlanNote = { type: string; body: string };

// Seller weight preference is stored as a base unit ('Ounces' or 'Grams'); the
// freight-details view displays totals in the corresponding major unit. Mirrors the
// wizard container's DISPLAY_WEIGHT_UNIT_BY_BASE.
const DISPLAY_WEIGHT_UNIT_BY_BASE: Record<BaseWeightUnit, WeightUnit> = {
  Ounces: 'Pounds',
  Grams: 'Kilograms',
};

/** The fields the two origin sources — the shipment's stamped address and a ship-from record — have in common. */
type OriginAddressSource = {
  name?: string | null;
  company?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  email?: string | null;
  ianaTimeZone?: string | null;
};

const toLtlAddress = (address: OriginAddressSource, fallbackCompany?: string | null): LtlAddress => ({
  name: address.name ?? '',
  company: address.company ?? fallbackCompany ?? '',
  line1: address.line1 ?? '',
  line2: address.line2 ?? '',
  city: address.city ?? '',
  state: address.state ?? '',
  postalCode: address.postalCode ?? '',
  countryCode: address.countryCode ?? DEFAULT_COUNTRY_CODE,
  phone: address.phone ?? '',
  email: address.email ?? '',
  locationType: '',
  ianaTimeZone: address.ianaTimeZone ?? null,
});

/**
 * Resolves the origin `LtlAddress` for a booked shipment, preferring the address stamped on the shipment at
 * booking time. Only the ship-from lookup was available before the shipment carried an origin, and it reads
 * the warehouse as it stands *today*: for a voided shipment whose ship-from was since edited it showed the new
 * address, and for a deleted one it showed no origin at all while the grid showed the stamped city.
 * Returns null when neither source has an address on file.
 */
const resolveOriginLtlAddress = (shipment: FreightShipment | null, shipFroms: ShipFrom[]): LtlAddress | null => {
  if (!shipment) return null;
  if (shipment.originAddress) return toLtlAddress(shipment.originAddress);

  const shipFrom = shipFroms.find((sf) => String(sf.shipFromId) === String(shipment.shipFromWarehouseId ?? ''));
  const sfAddr = shipFrom?.originAddress;
  if (!shipFrom || !sfAddr) return null;

  return toLtlAddress(sfAddr, shipFrom.name);
};

type BookingConfirmationViewProps = {
  freightShipmentId: string;
  bookingConfirmation: LtlBookingConfirmation | null;
  onClose: () => void;
  source?: LtlFreightBookingSource;
  isLoading?: boolean;
};

export const BookingConfirmationView = ({
  freightShipmentId,
  bookingConfirmation,
  onClose,
  source,
  isLoading = false,
}: BookingConfirmationViewProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  // The entities selector returns a runtime FreightShipment; the typed selector is
  // loosely typed, so cast to the canonical backend shape we feed the mapper.
  const rawShipment = useSelector((state) =>
    freightShipmentId ? Global.selectEntitiesFreightShipmentById(state, { freightShipmentId }) : null,
  ) as FreightShipment | null;

  const { orderNumber, salesOrderId } = useSelector((state) => {
    const fpId = rawShipment?.fulfillmentPlanId;
    if (!fpId) return { orderNumber: undefined, salesOrderId: undefined };
    const fp = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId: fpId });
    const fpSalesOrderId = fp?.salesOrdersFulfilledIds?.[0];
    const fpOrderNumber = fpSalesOrderId
      ? (Global.selectEntitiesSalesOrderProperty(state, { salesOrderId: fpSalesOrderId, property: 'orderNumber' }) ?? undefined)
      : undefined;
    return { orderNumber: fpOrderNumber, salesOrderId: fpSalesOrderId };
  }, shallowEqual);

  const orderNotes = useSelector((state) => {
    const fpId = rawShipment?.fulfillmentPlanId;
    if (!fpId) return emptyArray;
    return Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId: fpId, property: 'notes' }) ?? emptyArray;
  });
  const notes = useMemo<OrderNote[]>(
    () =>
      Object.entries(NOTE_TYPES)
        .map(([noteType, noteTypeLabel]) => ({
          label: intl.formatMessage(noteTypeLabel, { noteCount: SINGLE_NOTE_COUNT }),
          body: orderNotes.find((note: FulfillmentPlanNote) => note.type === noteType)?.body ?? '',
        }))
        .filter((note) => note.body !== ''),
    [intl, orderNotes],
  );

  const shipFroms: ShipFrom[] = useSelector(Global.selectSellerShipFroms);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const displayWeightUnit = DISPLAY_WEIGHT_UNIT_BY_BASE[weightUnit] ?? 'Pounds';
  const displayDimensionUnit = dimensionUnit;

  // Portal into ss-ui-client's ModalManager (inside `.app`) rather than Mantine's
  // default body target, so this Drawer shares a stacking context with the native
  // print modal it can launch (Print BOL). `useMaxZ` then orders them correctly.
  const modalManagerRef = useModalManagerPortal();
  const zIndex = useMaxZ();
  const { isOpen: isVoidModalOpen, open: openVoidModal, close: closeVoidModal } = useModal();
  const voidFreight = useVoidFreight();

  const resolvedOrigin = useMemo<LtlAddress | null>(() => {
    return resolveOriginLtlAddress(rawShipment, shipFroms);
  }, [rawShipment, shipFroms]);

  const ltlShipment = useMemo(
    () => (rawShipment ? mapBackendShipmentToLtlShipment(rawShipment, resolvedOrigin) : null),
    [rawShipment, resolvedOrigin],
  );

  const trackingDetailsByShipmentId = useSelector(Global.selectLtlTrackingDetailsByShipmentId);
  const trackingHistory = useMemo<FreightTrackingHistory | null>(() => {
    if (!ltlShipment) return null;
    const detail = trackingDetailsByShipmentId[ltlShipment.freightShipmentId];
    return {
      events: detail?.events ?? [],
      trackingUrl: detail?.trackingUrl ?? null,
    };
  }, [ltlShipment, trackingDetailsByShipmentId]);

  const onPrintDocument = useCallback(
    (document: LtlDocument) => {
      if (!ltlShipment) return;
      dispatch(downloadDocumentRequest(ltlShipment.freightShipmentId, document.key, document.documentType));
    },
    [dispatch, ltlShipment],
  );

  const trackedShipmentId = ltlShipment?.freightShipmentId;

  // Also invoked by TrackingHistorySection's staleness check, so this must stay referentially
  // stable across entity re-loads — depend on the id, not the mapped shipment.
  const onTrack = useCallback(() => {
    if (!trackedShipmentId) return;
    dispatch(trackShipmentRequest(trackedShipmentId));
  }, [dispatch, trackedShipmentId]);

  const handleOrderLinkClick = useCallback(() => {
    if (!salesOrderId || !rawShipment?.fulfillmentPlanId) return;
    dispatch(
      goToOrderDetails({ salesOrderId, fulfillmentPlanId: rawShipment.fulfillmentPlanId, selectOrderInGrid: false, replaceRoute: false }),
    );
  }, [dispatch, rawShipment, salesOrderId]);

  const handleVoidConfirm = useCallback(async () => {
    closeVoidModal();
    const fulfillmentPlanId = rawShipment?.fulfillmentPlanId;
    if (!fulfillmentPlanId) return;
    const didSucceed = await voidFreight([fulfillmentPlanId]);
    if (didSucceed) {
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_VOID, {
        fulfillment_plan_id: fulfillmentPlanId,
        source,
      });
      dispatch(getFreightGridRequest());
      onClose();
    }
  }, [closeVoidModal, dispatch, onClose, rawShipment, source, voidFreight]);

  // An already-voided shipment cannot be voided again, so the menu item is left without a handler
  // and FreightDetails renders it disabled.
  const canVoid = Boolean(rawShipment?.fulfillmentPlanId) && !(ltlShipment && isShipmentCancelled(ltlShipment));

  const portalProps = useMemo(() => ({ target: modalManagerRef ?? undefined }), [modalManagerRef]);

  if (!ltlShipment || isLoading) {
    return (
      <Drawer
        classNames={{ content: style['drawer-content'], header: style['drawer-header'], title: style['drawer-title'] }}
        data-ignore-root-focus
        onClose={onClose}
        opened
        padding={0}
        portalProps={portalProps}
        position='right'
        size='100%'
        title={intl.formatMessage(bookingConfirmationViewMessages.title)}
        zIndex={zIndex}
      >
        <LoadingOverlay visible />
      </Drawer>
    );
  }

  return (
    <>
      <FreightDetails
        bookingConfirmation={bookingConfirmation}
        displayDimensionUnit={displayDimensionUnit}
        displayWeightUnit={displayWeightUnit}
        isDocumentsLoading={rawShipment?.documents === null}
        isOpen
        notes={notes}
        onClose={onClose}
        onOrderLinkClick={orderNumber ? handleOrderLinkClick : undefined}
        onPrintDocument={onPrintDocument}
        onTrack={onTrack}
        onVoid={canVoid ? openVoidModal : undefined}
        orderNumber={orderNumber}
        originAddress={resolvedOrigin}
        portalRef={modalManagerRef ?? null}
        shipment={ltlShipment}
        trackingHistory={trackingHistory}
        zIndex={zIndex}
      />
      {isVoidModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...freightActionButtonMessages.voidConfirmBody} />}
          buttonMessage={<FormattedMessage {...freightActionButtonMessages.voidConfirmButton} />}
          buttonTheme='danger'
          header={<FormattedMessage {...freightActionButtonMessages.voidConfirmHeader} />}
          onClose={closeVoidModal}
          onConfirm={handleVoidConfirm}
        />
      )}
    </>
  );
};
